import { resolveFriendlyToolLabel } from 'app/pages/loanApplication/tabs/aiChat/components/ActionPlan/ActionPlan.constants';
import _ from 'lodash';

import { formatPriorToDisplayName } from 'constants/condition';
import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';
import { formatDate } from 'utils/date';
import { formatBooleanValue } from 'utils/format';
import { jsonParse, jsonStringify } from 'utils/json';
import { formatNumberToCurrencyString } from 'utils/number';

import {
  EDIT_TOOL_PREFIX,
  FIELD_LABEL_OVERRIDES,
  MONEY_FIELD_KEYS,
  NO_ARGUMENT_ACTION_SUMMARIES,
  PENDING_TOOL_CALL_TOOL,
  SKIPPED_ARG_KEYS,
  STAGED_CHANGES_REVIEW_TOOLS,
  STEP_PENDING_TOOL_CALL_LABELS,
} from './StepPendingToolCall.constants';

// Money fields are whole-dollar loan amounts — drop the noisy trailing cents.
const WHOLE_DOLLAR_CURRENCY_OPTIONS = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
// Matches an ISO date/date-time (2026-07-02 or 2026-07-02T00:00:00Z) so a plain
// numeric-looking string is never mistaken for a date and reformatted.
const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}([T ]|$)/;
// A bare code-style identifier (no spaces/punctuation) that carries a camelCase
// or PascalCase boundary — e.g. a backend enum value like `SingleFamily`. Split
// only these so free text ("123 Main St", "John") is left untouched.
const BARE_IDENTIFIER_PATTERN = /^[A-Za-z0-9]+$/;
const CAMEL_BOUNDARY_PATTERN = /[a-z0-9][A-Z]/;

// The REST contract and the SignalR payload both carry `toolArguments` as a JSON
// string. Live mock fixtures / Storybook may hand us an already-parsed object, so
// tolerate both — and any malformed JSON — without throwing.
export const parseToolArgumentsObject = toolArguments => {
  if (_.isNil(toolArguments)) {
    return null;
  }

  if (_.isPlainObject(toolArguments)) {
    return toolArguments;
  }

  if (_.isString(toolArguments)) {
    try {
      const parsed = jsonParse(toolArguments);

      return _.isPlainObject(parsed) ? parsed : null;
    } catch {
      return null;
    }
  }

  return null;
};

const prettyPrintArguments = toolArguments => {
  if (_.isNil(toolArguments)) {
    return null;
  }

  const parsed = parseToolArgumentsObject(toolArguments);

  if (parsed) {
    try {
      return jsonStringify(parsed, null, 2);
    } catch {
      // fall through to raw string handling
    }
  }

  if (_.isString(toolArguments)) {
    return toolArguments;
  }

  return String(toolArguments);
};

const firstNonEmptyString = candidates => {
  const match = candidates.find(value => _.isString(value) && value.trim().length > 0);

  return match ?? null;
};

// Be liberal in what fields we accept — the BE condition tool args have shifted
// naming before, so map a few likely synonyms onto the display shape.
export const normalizeCondition = condition => {
  if (!_.isPlainObject(condition)) {
    return null;
  }

  const internalText = firstNonEmptyString([
    condition.internalText,
    condition.internal,
    condition.message,
  ]);
  const borrowerText = firstNonEmptyString([
    condition.borrowerText,
    condition.borrowerFacingText,
    condition.borrower,
  ]);
  const priorToRaw = firstNonEmptyString([
    condition.priorToType,
    condition.conditionPriorToType,
    condition.priorTo,
  ]);

  if (_.isNil(internalText) && _.isNil(borrowerText) && _.isNil(priorToRaw)) {
    return null;
  }

  return {
    internalText,
    borrowerText,
    // Raw `priorTo` (e.g. `ApprovalClosing`) round-trips to the backend as
    // `editedPriorToType`; `priorTo` is the friendly display name.
    priorToRaw,
    priorTo: priorToRaw ? formatPriorToDisplayName(priorToRaw) : null,
  };
};

export const extractConditions = parsedArgs => {
  const rawList =
    (_.isArray(parsedArgs?.conditions) && parsedArgs.conditions) ||
    (_.isArray(parsedArgs?.items) && parsedArgs.items) ||
    (_.isArray(parsedArgs) && parsedArgs) ||
    [];

  return rawList
    .map((raw, index) => {
      const condition = normalizeCondition(raw);

      // The positional `index` is the contract's `ConditionDecision.index` — the
      // index into the proposed `conditions[]`. Capture it BEFORE filtering so a
      // drop-able/unrecognized element does not shift every later row relative to
      // the backend array (decisions key per-condition on this index).
      return (
        condition && {
          ...condition,
          index,
          key: `${condition.internalText ?? condition.borrowerText ?? 'condition'}-${index}`,
        }
      );
    })
    .filter(Boolean);
};

// `add_conditions_from_library` args carry only ids — a library conditionId plus
// per-task taskTypeId/assigneeId — so the display text (message/borrower/priorTo)
// and task/borrower names are resolved from the store, not the args (see
// useResolvedLibraryConditions). This just normalizes the id-bearing shape and
// keeps the positional `index` the backend keys ConditionDecisions on.
export const extractLibraryConditions = parsedArgs => {
  const rawList = (_.isArray(parsedArgs?.conditions) && parsedArgs.conditions) || [];

  return rawList
    .map((raw, index) => {
      if (!_.isPlainObject(raw) || _.isNil(raw.conditionId)) {
        return null;
      }

      const tasks = (_.isArray(raw.tasks) ? raw.tasks : []).filter(_.isPlainObject).map(task => ({
        taskTypeId: task.taskTypeId ?? null,
        assigneeId: task.assigneeId ?? null,
        relationEntityId: task.relationEntityId ?? null,
      }));

      return {
        conditionId: String(raw.conditionId),
        // The model may fill the template's blanks (messageOverride) or override
        // the template prior-to; both are optional and win over the library value.
        messageOverride: firstNonEmptyString([raw.messageOverride]),
        borrowerMessageOverride: firstNonEmptyString([raw.borrowerMessageOverride]),
        priorToOverrideRaw: firstNonEmptyString([raw.priorToType]),
        tasks,
        index,
        key: `library-${raw.conditionId}-${index}`,
      };
    })
    .filter(Boolean);
};

const isGuidString = value => _.isString(value) && GUID_PATTERN.test(value);

// Split a camelCase / PascalCase / snake_case identifier into Title-Case words,
// preserving all-caps acronyms (REO, MI). Used for both field keys and bare
// enum values like `SingleFamily` -> "Single Family".
const humanizeIdentifier = text =>
  text
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map(word =>
      word === word.toUpperCase()
        ? word
        : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
    )
    .join(' ');

const humanizeFieldKey = key => FIELD_LABEL_OVERRIDES[key] ?? humanizeIdentifier(key);

const formatStringValue = value => {
  if (ISO_DATE_PATTERN.test(value)) {
    return formatDate(value, SHORT_MONTH_DAY_YEAR_FORMAT);
  }

  // A bare camel/Pascal-case code is a backend enum — humanize it; leave real
  // free text (has spaces or punctuation) as-is.
  return BARE_IDENTIFIER_PATTERN.test(value) && CAMEL_BOUNDARY_PATTERN.test(value)
    ? humanizeIdentifier(value)
    : value;
};

// Render a single primitive as human-readable text: ISO dates -> "Jul 2, 2026",
// booleans -> Yes/No, money fields -> "$540,000", other numbers grouped, and
// enum-like strings de-camel-cased.
const formatScalarValue = (key, value) => {
  if (_.isBoolean(value)) {
    return formatBooleanValue(value);
  }

  if (_.isNumber(value)) {
    return MONEY_FIELD_KEYS.has(key)
      ? formatNumberToCurrencyString(value, WHOLE_DOLLAR_CURRENCY_OPTIONS)
      : value.toLocaleString('en-US');
  }

  return formatStringValue(value);
};

// A scalar carries no reviewer-facing meaning when it is empty or a raw GUID id.
const isSummarizableScalar = value =>
  (_.isString(value) && value.trim().length > 0 && !isGuidString(value)) ||
  _.isNumber(value) ||
  _.isBoolean(value);

const formatArrayValue = (key, items) => {
  const primitives = items.filter(item => isSummarizableScalar(item));

  // A short list of plain values reads best inline; anything else (GUID lists,
  // object arrays) collapses to a count so the summary stays scannable.
  if (primitives.length === items.length && primitives.length > 0) {
    return primitives.map(item => formatScalarValue(key, item)).join(', ');
  }

  return STEP_PENDING_TOOL_CALL_LABELS.SELECTED_COUNT({ count: items.length });
};

// Walk a patch object into a flat list of { key, parentLabel, value } display
// rows: recurse into nested objects, collapse arrays to one row, and drop
// plumbing keys, empty containers, nulls, and raw GUID ids that mean nothing to
// a reviewer. `parentLabel` carries the humanized name of an enclosing
// sub-object so sibling objects that share a leaf key stay distinguishable.
const flattenPatchArgs = (value, parentLabel) => {
  if (!_.isPlainObject(value)) {
    return [];
  }

  return _.flatMap(Object.entries(value), ([key, entryValue]) => {
    if (SKIPPED_ARG_KEYS.has(key) || _.isNil(entryValue)) {
      return [];
    }

    if (_.isArray(entryValue)) {
      return entryValue.length > 0
        ? [{ key, parentLabel, value: formatArrayValue(key, entryValue) }]
        : [];
    }

    if (_.isPlainObject(entryValue)) {
      if (_.isEmpty(entryValue)) {
        return [];
      }

      // Top level: strip the single wrapper key — it IS the tool subject (e.g.
      // `closing`). Deeper: carry the humanized parent so sibling sub-objects
      // that share a leaf key (mailingAddress.street vs currentAddress.street)
      // render distinct "Mailing Address — Street" / "Current Address — Street".
      const childLabel = _.isUndefined(parentLabel) ? null : humanizeFieldKey(key);

      return flattenPatchArgs(entryValue, childLabel);
    }

    return isSummarizableScalar(entryValue)
      ? [{ key, parentLabel, value: formatScalarValue(key, entryValue) }]
      : [];
  });
};

export const buildEditPatchFieldChanges = toolArguments => {
  const parsed = parseToolArgumentsObject(toolArguments);

  if (_.isNil(parsed)) {
    return [];
  }

  return flattenPatchArgs(parsed).map(({ key, parentLabel, value }, index) => {
    const leafLabel = humanizeFieldKey(key);

    return {
      key: `${key}-${index}`,
      label: parentLabel ? `${parentLabel} — ${leafLabel}` : leafLabel,
      value,
    };
  });
};

export const buildStepPendingToolCallView = pendingToolCall => {
  if (_.isNil(pendingToolCall) || _.isEmpty(pendingToolCall.toolName)) {
    return null;
  }

  const { toolName, toolArguments } = pendingToolCall;
  const friendlyName =
    resolveFriendlyToolLabel(toolName) ?? STEP_PENDING_TOOL_CALL_LABELS.UNKNOWN_TOOL;
  const parsedArgs = parseToolArgumentsObject(toolArguments);

  if (toolName === PENDING_TOOL_CALL_TOOL.ADD_CONDITIONS_FROM_LIBRARY) {
    const libraryConditions = extractLibraryConditions(parsedArgs);

    if (libraryConditions.length > 0) {
      return {
        friendlyName,
        conditions: [],
        libraryConditions,
        fieldChanges: [],
        actionSummary: null,
        formattedArguments: null,
      };
    }
  }

  if (toolName === PENDING_TOOL_CALL_TOOL.ADD_ADHOC_CONDITIONS) {
    const conditions = extractConditions(parsedArgs);

    if (conditions.length > 0) {
      return {
        friendlyName,
        conditions,
        libraryConditions: [],
        fieldChanges: [],
        actionSummary: null,
        formattedArguments: null,
      };
    }
  }

  if (toolName.startsWith(EDIT_TOOL_PREFIX)) {
    const fieldChanges = buildEditPatchFieldChanges(toolArguments);

    if (fieldChanges.length > 0) {
      return {
        friendlyName,
        conditions: [],
        libraryConditions: [],
        fieldChanges,
        actionSummary: null,
        formattedArguments: null,
      };
    }
  }

  // LEN-41: an empty-object payload ("{}") carries nothing to review — a no-arg
  // finalize tool (e.g. commit_loan_changes) lands here. Suppress the "{}" args
  // block and surface the tool's plain-language action summary instead, so the
  // confirmation always states what confirming does rather than showing nothing.
  const hasEmptyObjectArgs = _.isPlainObject(parsedArgs) && _.isEmpty(parsedArgs);
  const actionSummary = NO_ARGUMENT_ACTION_SUMMARIES[toolName] ?? null;

  return {
    friendlyName,
    conditions: [],
    libraryConditions: [],
    fieldChanges: [],
    actionSummary,
    isStagedChangesReview: STAGED_CHANGES_REVIEW_TOOLS.has(toolName),
    formattedArguments: hasEmptyObjectArgs ? null : prettyPrintArguments(toolArguments),
  };
};
